import { CellClassParams, RowNode } from "ag-grid-community"
import { ExtraColDefParams, iRmbxColDef, tColDefType } from "../../components/custom-dashboards/settings-files/types"
import { alertCellClassRule, getAvailableStatusValues, timekeepingDataIsEditable, timekeepingRowIsEditable } from "../../common/ag-grid-utils"
import {
    checkboxColDefCreator,
    colDefCreator,
    dateColDefCreator,
    editableTimeCardColDefCreator,
    employeeColDefCreator,
    projectColDefCreator,
    timekeepingStatusColDefCreator,
} from "../../components/custom-dashboards/settings-files/standard-col-def-creators"
import { convertJsonPointerToDotPath, convertTimezoneToUtc, getFilteredProjects, getRoundUpTime } from "../../common/ts-utils"
import { FieldFormTimeType, nestedObject, tColDef, tRateMatchDependency, tRateMatchDependencyMap, tRateMatchTarget, tTableSettings, tableObject } from "../types"
import { timekeepingDisabledTooltip, timekeepingEntryGeneralErrorsTooltip } from "../../common/ag-grid-custom-tooltip-formatters"

import { STORED_DATE_ONLY_FORMAT } from "../../common/constants"
import { filterHasSingleValueSelected } from "../../filters/utils"
import { format } from "date-fns"
import { getCombinedSchema } from "./schema-manipulation"
import { getFlagEnabled } from "../../getFlagValue"
import { getSelectorActionButtons } from "../../components/custom-dashboards/settings-files/action-button-settings"
import { iEmployeeWorkShiftFormSchema } from "../../cached-data/types"
import { JsonPointer as jsonpointer } from "json-ptr"
import { lockedPeriodCellClassRule } from "../../common/ag-grid-ts-utils"
import { referenceablesToValueFormatters } from "../../common/referenceable-value-formatters"
import { renderFieldTemplate } from "../templates/HandlebarsCompactTemplate"
import { tContext } from "../../components/custom-dashboards/types"
import { togglePlaceholdersVisible } from "../../actions"
import { workShiftDateDefaultTimeValueSetter } from "../../common/ag-grid-value-setters"
import { editTimecardDetailsAction } from "../../components/custom-dashboards/settings-files/common-button-defs"
import { RateType } from "../../common/types"

/**
 * Widget types that only affect mobile rendering. On web, these are treated as
 * plain objects so their sub-properties generate individual AG-Grid columns.
 */
const MOBILE_ONLY_WIDGETS = ["rate-row"]

const removeBtn = [
    {
        label: "Remove",
        icon: "delete",
        action: "deleteSelectedRows",
        args: {
            local_state_update: true,
        },
    },
]
const addBtn = [
    {
        label: "Add Row",
        icon: "add",
        action: "addNewRow",
    },
]
const showHidePlaceholders = (cico: boolean) =>
    cico
        ? [
              {
                  label: "Show/Hide ▾",
                  icon: "view",
                  args: {
                      extraArgs: {
                          options: [
                              {
                                  label: "Placeholders",
                                  action: togglePlaceholdersVisible,
                                  // The state parameter we look at to see if this should be
                                  // checked on
                                  activeStateParams: ["placeholdersVisible"],
                              },
                          ],
                      },
                  },
                  checklist: true,
              },
          ]
        : [
              {
                  label: "Show Placeholders",
                  action: "togglePlaceholders",
                  icon: "view",
                  customButtonType: "placeholders",
              },
          ]

/**
 * This method takes in a schema section and creates a collection of column definitions and table settings
 * based on the given schema.
 * @param arraySchema a portion of the UI schema defining the columns
 * @param resource which rhumbix resource are we referencing - ex. employee, material, equipment
 * @param editable should the list be editable?
 * @returns an object containing the table settings based on a schema
 */
export const getSettingsForSchema = (
    arraySchema: tableObject,
    resource: string,
    editable: boolean,
    useLocalState?: boolean,
    defaultContext?: Record<"employee" | "project", Record<string, any>>
): tTableSettings => {
    const customTable = arraySchema["ui:custom-table"]
    const schemaColDefs = getColDefsForSchema({ ...arraySchema, editable }, "", defaultContext) ?? []
    // For Field Form schemas, we don't want to confine the changes to local state. That's only for
    // shift extras which use the weekly tk detail modal
    const removeButton = {
        ...removeBtn[0],
        args: {
            ...removeBtn[0].args,
            local_state_update: useLocalState,
        },
    }

    // Auto-discover rate matching dependencies when wage rates feature is enabled.
    // The actual matching is additionally gated by currentUser.features.rate_tables in FormList.
    const rateMatchDependencyMap = getFlagEnabled("WA-9079-wage-rates") ? buildRateMatchDependencyMap(schemaColDefs) : undefined

    return {
        resources: [resource],
        colDefs: [...(editable ? [checkboxColDefCreator({})] : []), ...schemaColDefs],
        relatedNames: {
            employeeEntries: {
                employees: ["/employee"],
                projects: ["/project"],
            },
        },
        gridSettings: {
            defaultColDef: {
                editable: editable,
                cellClassRules: {
                    "alert-cell": alertCellClassRule,
                },
            },
            enableRangeSelection: true,
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            ...((customTable && customTable.tableSettings) || {}),
        },
        otherSettings: {
            enableClipboard: editable,
            sizeColumnsToFitSize: 915,
            rowLevelErrorDisplay: true,
            buttons: {
                cell: [],
                row: editable ? [removeButton] : [],
                table: editable ? [...addBtn] : [],
            },
        },
        rateMatchDependencyMap,
    }
}

/**
 * Given a list's items schema section return any field names the user has viewable: false permissions
 * @param listItems initial input is the `items` key from the arraySchema
 * @param passedKey leave blank, populated on recursion
 * @param blockedKeys leave blank, generated and returned when called
 * @returns list of all keys were viewable permissions are set to false.
 */
export const getNonViewableFields = (listItems: Record<string, any>, passedKey = "", blockedKeys: string[] = []): string[] => {
    if (listItems?.properties) {
        Object.keys(listItems.properties).forEach(key => {
            const newPassKey = passedKey ? `${passedKey}.${key}` : key
            getNonViewableFields(listItems.properties[key], newPassKey, blockedKeys)
        })
    } else if (["string", "integer", "number", "boolean"].includes(listItems?.type) && listItems?.viewable === false) {
        blockedKeys.push(passedKey)
    }
    return blockedKeys
}

/**
 * Checks whether a virtual column definition references any non-viewable fields.
 * Handles both valueGetter strings (dot-separated field references) and
 * priceMapping arrays (slash-separated paths used by pricing-total columns).
 *
 * @param blockedFields - List of dot-separated non-viewable field paths from getNonViewableFields
 * @param schemaColDef - The virtual column definition to check
 * @returns true if the column references any non-viewable fields
 */
export const virtualColDefReferencesBlockedFields = (blockedFields: string[], schemaColDef: Record<string, any>): boolean => {
    if (!blockedFields?.length) return false

    const virtualValueGetter: string = schemaColDef?.valueGetter
    if (virtualValueGetter && blockedFields.some(fieldName => virtualValueGetter.includes(fieldName))) {
        return true
    }

    if (getFlagEnabled("WA-9079-wage-rates") && schemaColDef?.priceMapping) {
        const priceMappingFields: string[] = schemaColDef.priceMapping.flatMap((mapping: { rates?: string[]; quantities?: string[] }) => [...(mapping.rates ?? []), ...(mapping.quantities ?? [])])
        return blockedFields.some(fieldName => priceMappingFields.some(path => convertJsonPointerToDotPath(path).includes(fieldName)))
    }

    return false
}

/**
 * @param arraySchema a portion of the UI schema defining the columns
 * @param fieldPrefix - Used for validation of nested list fields
 * @returns object with information on the column definitions in the schema
 */
export const getColDefsForSchema = (arraySchema: tableObject, fieldPrefix = "", defaultContext?: Record<"employee" | "project", Record<string, any>>): (tColDef | iRmbxColDef)[] => {
    const defaultColDefs = getDefaultColDefsForSchema(arraySchema.items, fieldPrefix)
    const schemaColDefs = arraySchema["ui:custom-table"].colDefs
    if (!schemaColDefs) {
        return defaultColDefs
    }

    const derivedColDefs = schemaColDefs.map((schemaColDef: any) => {
        if ("width" in schemaColDef) {
            schemaColDef.minWidth = schemaColDef.width
        }
        // If a field is given, get the default column definition for that field
        if (schemaColDef.field) {
            const schemaColDefField = schemaColDef.field
            const schemaComponentType = schemaColDef?.componentType
            const defaultColDef = getDefaultColDefForField(schemaColDefField, arraySchema.items, undefined, schemaComponentType, fieldPrefix, arraySchema?.["editable"], defaultContext)
            return {
                ...defaultColDef,
                ...schemaColDef,
                field: schemaColDefField,
            }
        }

        /**
         * virtual fields - these are fields that traditionally hold calculated fields - such as total fields.
         * These fields do aggregations of store data, but aren't mapped to a specific field in the store
         * Based on the dataType defined in the colDef - get the proper default column definition
         */

        // collect any fields in the list the user can't view, we will block any dynamic columns using those fields
        // we do this because we cannot set field permissions for fields that don't exist in the schema
        const blockedFields = getNonViewableFields(arraySchema.items)

        // check that they have viewable access to all the fields referenced by this virtual column
        const matchesBlockedFields = virtualColDefReferencesBlockedFields(blockedFields, schemaColDef)

        let virtualDefaultColDef = {}
        if (schemaColDef.dataType || ["pricing-total"].includes(schemaColDef.componentType)) {
            virtualDefaultColDef = getDefaultColDefForField(
                schemaColDef.headerName,
                arraySchema.items,
                schemaColDef.dataType,
                schemaColDef.componentType,
                fieldPrefix,
                false, // we always want calculated columns to be uneditable
                defaultContext
            )
        }
        return {
            ...virtualDefaultColDef,
            ...schemaColDef,
            editable: false,
            field: undefined,
            hide: matchesBlockedFields,
        }
    })

    /**
     * hidden colDefs - this is needed for all the fields that we don't expose in the table, but still want to
     * keep the information - ex. All the employee information fields - we only show 2-3 fields, but want
     * to keep all the employee information in the store. So we have hidden virtual fields holding onto it
     * so when we save the grid value back to the form, it has all the necessary information
     */
    const missingColDefs = defaultColDefs?.filter(defaultColDef => {
        return derivedColDefs.findIndex((derivedColDef: { field: string }) => derivedColDef.field === defaultColDef.field) === -1
    })
    const hiddenColDefs = missingColDefs?.map(missingColDef => {
        return {
            ...missingColDef,
            hide: true,
        }
    })
    if (hiddenColDefs) derivedColDefs.push(...hiddenColDefs)
    return derivedColDefs
}

/**
 * This is used to define column definitions for calculated fields that aren't tied directly to a store field
 * Given the dataType of the field provide the proper colDef.
 * It will also check to see if the UI schema indicates any additional customization.
 * @param propertyPath - This is the header name for the calculated column
 * @param objectSchema - Section of the schema defining list item properties and requiredness
 * @param dataType - what type of data will be in the cell - options are integer, number, or string
 * @param componentType - currently only have currency - TODO: expand to match more ListView components
 * @param fieldPrefix - Used for validation of nested list fields
 * @param parentTableEditable Optional. If false, all cells in the row will be locked.
 * @returns a list of colDefs for calculat
 */
const getDefaultColDefForField = (
    propertyPath: string,
    objectSchema?: nestedObject,
    dataType?: string,
    componentType?: string,
    fieldPrefix = "",
    parentTableEditable?: boolean,
    defaultContext?: Record<"employee" | "project", Record<string, any>>
) => {
    //This is meant to handle calculated columns that comes through like "Total"
    const pathList = propertyPath.startsWith("/") ? jsonpointer.decode(propertyPath) : [propertyPath]
    const schemaProperty = pathList.reduce((a, b) => a?.properties?.[b], objectSchema)
    const parentObjectSchema = pathList.slice(0, -1).reduce((a, b) => a?.properties?.[b], objectSchema)
    const requiredList = parentObjectSchema?.required ?? []
    const isRequired = requiredList.includes(`${pathList[pathList.length - 1]}`)

    const editable = parentObjectSchema?.editable !== false && parentTableEditable

    return mapUISchemaComponentToColDefs(propertyPath, schemaProperty, dataType, componentType, isRequired, fieldPrefix, editable, defaultContext)[0]
}

/**
 *
 * @param objectSchema - Section of the schema defining requiredness, order, and subfields
 * @param fieldPrefix - Used for validation of nested list fields
 * @param parentTableEditable Optional. If false, all cells in the row will be locked.
 * @returns returns a list of colDefs based on the provided schema
 */
const getDefaultColDefsForSchema = (
    objectSchema: Record<string, any>,
    fieldPrefix = "",
    parentTableEditable?: boolean,
    defaultContext?: Record<"employee" | "project", Record<string, any>>
): iRmbxColDef[] => {
    const { properties, order, required, "ui:order": uiOrder } = objectSchema
    if (!properties) return []
    const fieldOrder = order ?? uiOrder
    const schemaPropertyNames = Object.keys(properties)
    if (fieldOrder?.length)
        schemaPropertyNames.sort((a: string, b: string): number => {
            return fieldOrder.indexOf(a) - fieldOrder.indexOf(b)
        })
    const schemaColDefs = []
    for (const propertyName of schemaPropertyNames) {
        const schemaProperty = properties[propertyName]
        const isRequired = required && required.includes(propertyName)
        const colDefs = mapUISchemaComponentToColDefs(propertyName, schemaProperty, undefined, undefined, isRequired, fieldPrefix, parentTableEditable, defaultContext)
        schemaColDefs.push(...colDefs)
    }
    return schemaColDefs
}

/**
 * Checks if the column type is a resource selector
 * @param colType - the type of column - will be a selector widget name if it's a match
 * @returns true if the column type is a resource selector, false otherwise
 */
const isResourceSelector = (colType: string): boolean => {
    return ["employee-selector", "material-selector", "equipment-selector", "cost-code-selector", "laborrate-selector", "materialrate-selector", "equipmentrate-selector"].includes(colType)
}

/**
 * Updates column definitions based on UI schema
 * @param propertyName - path to value in the list item
 * @param schemaProperty - section of the UI schema defining which component to use and any special settings
 * @param dataType - what type of data will be in the cell
 * @param componentType - currently only have currency
 * @param isRequired - is the field required
 * @param fieldPrefix - Used for validation of nested list fields
 * @param parentTableEditable Optional. If false, all cells in the row will be locked.
 * @returns Column definitions based on the UI schema
 */
const mapUISchemaComponentToColDefs = (
    propertyName: string,
    schemaProperty?: Record<string, any>,
    dataType?: string,
    componentType?: string,
    isRequired?: boolean,
    fieldPrefix = "",
    parentTableEditable?: boolean,
    defaultContext?: Record<"employee" | "project", Record<string, any>>
): iRmbxColDef[] => {
    // can't use destructuring bc schemaProperty doesn't exist for virtual columns for things like totals
    let colWidget = schemaProperty?.["ui:widget"]
    if (colWidget === "select") {
        // some old shift extras have a ui:widget for "select", which is invalid. Schema migration removes this,
        // but if the WA-6290 flag is activated before migrations - we want to skip it using select.
        colWidget = "string"
    }
    const properties = schemaProperty?.["properties"]
    // our permissions only define editable: false, otherwise they inherit from the parent
    // so we only want to mark the column uneditable if the permissions explicitly say false (not undefined)
    let notEditable = schemaProperty?.["editable"] === false || parentTableEditable === false
    const notViewable = schemaProperty?.["viewable"] === false
    const type = schemaProperty?.["type"]
    const defaultTemplate = schemaProperty?.["default"]

    const fieldMin = schemaProperty?.min
    const fieldMax = schemaProperty?.max

    const { "ui:minute-interval": minInterval } = schemaProperty ?? {}
    // make sure value aligns with field's minute interval from schema or it will use default interval of 15
    // get proper rounded time and then convert to a UTC string for the store
    const formInfo = {
        template: defaultTemplate,
        context: {
            ...(defaultContext ?? {}),
            today: format(new Date(), STORED_DATE_ONLY_FORMAT),
            // we keep this a date object so we can adjust the minute intervals based on schema settings
            now: getRoundUpTime(minInterval, convertTimezoneToUtc(new Date())).toISOString(),
        },
    }
    const defaultVal = defaultTemplate && getFlagEnabled("WA-8350-ff-list-defaults") ? renderFieldTemplate(formInfo) : undefined

    // Schema properties may reference either the employee or the project attached to the
    // schema by providing a `referenceableTable` attribute of either "employee" or "project" respectively.
    // Here is an example:
    // {"classification": {"type": "string", "title": "Employee Classification",
    // "editable": false, "referenceableTable": "employee"}
    const referenceableTable = schemaProperty?.["referenceableTable"]

    // store any extra column definition settings in extraParams
    const extraParams = {} as ExtraColDefParams
    if (componentType) {
        colWidget = componentType
    } else if (colWidget && schemaProperty) {
        const { "ui:minute-interval": minuteInterval, "ui:military-time": militaryTime } = schemaProperty
        if (colWidget === "datetime-picker" && minuteInterval) {
            extraParams["minuteInterval"] = minuteInterval
        }
        if (colWidget === "datetime-picker" && militaryTime) {
            extraParams["militaryTime"] = militaryTime
        }
        if (colWidget === "laborrate-selector" && schemaProperty?.["ui:time-type"]) {
            extraParams.timeType = schemaProperty?.["ui:time-type"]
        }
    }
    // get the type of column for the table - use supplied dataType first, fallback to schema definition type
    let columnDataType = dataType ?? type

    // remove this comment and the if condition below with WA-8706-guest-mode-status
    // we don't have bad schemas anymore, and this could cause errors if there are permissions for fields not in use
    // this can happen when using common permission sets, but having some fields removed based on client needs
    if (!getFlagEnabled("WA-8706-guest-mode-status") && !columnDataType) {
        // Default to object. Hack to deal with bad schemas for now :(
        columnDataType = "object"
    }
    // if there is a widget, use that, otherwise fallback to data type for column definition
    const colType = colWidget ?? columnDataType

    if ((colType === "object" || MOBILE_ONLY_WIDGETS.includes(colType)) && schemaProperty) {
        const { order, "ui:order": uiOrder, required } = schemaProperty
        const fieldOrder = order ?? uiOrder
        const schemaPropertyNames = Object.keys(properties)
        if (fieldOrder) {
            schemaPropertyNames.sort((a, b) => {
                return fieldOrder.indexOf(a) - fieldOrder.indexOf(b)
            })
        }
        return schemaPropertyNames.reduce((colDefs, subPropertyName) => {
            const subSchemaProperty = properties[subPropertyName]
            const subFieldPrefix = `${fieldPrefix}${propertyName}/`
            const isSubRequired = required?.includes(subPropertyName)
            const updatedColDef = mapUISchemaComponentToColDefs(subPropertyName, subSchemaProperty, undefined, undefined, isSubRequired, subFieldPrefix, !notEditable, defaultContext)
            if (updatedColDef) colDefs.push(...updatedColDef)
            return colDefs
        }, [] as iRmbxColDef[])
    }

    const propertyNameSlashEscaped = getFlagEnabled("WA-7988-fix-ff-list-sort") ? propertyName.replace(/\//g, "") : propertyName.replace(/\//g, "~1")

    if (referenceableTable) fieldPrefix = ""

    const header = schemaProperty?.["title"] ?? propertyName
    const skipProjectFilter = !!schemaProperty?.["ui:skip-project-filter"]

    if (isResourceSelector(colType)) {
        // if using a selector, if the id is not editable, then the selector is not editable
        // allows editing of things like pricing fields, which are nested on the resource (emp, equip, mat, cc),
        // without allowing a new resource selection.
        notEditable = notEditable || schemaProperty?.properties?.id?.editable === false
    }
    const colDef = colDefCreator({
        type: colType as tColDefType,
        extraParams,
        headerName: header,
        headerTooltip: schemaProperty ? schemaProperty.headerTooltip : undefined,
        field: `/${fieldPrefix}${propertyNameSlashEscaped}`,
        required: isRequired,
        enum: schemaProperty ? schemaProperty.enum : undefined,
        hide: schemaProperty?.hidden || notViewable === true,
        skipProjectFilter: skipProjectFilter,
        fieldMin,
        fieldMax,
        unique: isResourceSelector(colType),
        editable: !notEditable,
        default: defaultVal,
    })
    if (referenceableTable)
        colDef.referenceableMap = {
            referenceableKey: `${referenceableTable}s`,
            rowKey: `${referenceableTable}`,
        }
    if (skipProjectFilter) {
        colDef.ignoreRelatedFilters = ["projectId"]
    }
    return [colDef]
}

/**
 * Maps rate selector resource names to the resource names of the selectors
 * that should trigger rate re-matching when changed.
 */
const RATE_SELECTOR_TRIGGER_MAP: Record<string, string[]> = {
    laborRates: ["employees", "costCodes"],
    equipmentRates: ["equipment", "costCodes"],
    materialRates: ["materials", "costCodes"],
}

/**
 * Scan generated col defs for rate selector columns and their associated trigger columns
 * to build a rate matching dependency map. Uses convention-based auto-discovery:
 * each rate selector type (labor, equipment, material) knows which other selector types
 * should trigger re-matching when their values change.
 *
 * @param colDefs - The full list of col defs generated for the table
 * @returns A dependency map array if any rate dependencies are found, otherwise undefined
 */
export const buildRateMatchDependencyMap = (colDefs: (tColDef | iRmbxColDef)[]): tRateMatchDependencyMap | undefined => {
    // Index all colDef fields by their resourceName for quick lookup
    const fieldsByResource: Record<string, string[]> = {}
    // Collect rate selector targets grouped by their resourceName
    const rateTargetsByResource: Record<string, tRateMatchTarget[]> = {}

    for (const colDef of colDefs) {
        const resourceName = (colDef as iRmbxColDef).resourceName
        const field = colDef.field

        if (!field || !resourceName) continue

        // Track all fields by resource
        if (!fieldsByResource[resourceName]) {
            fieldsByResource[resourceName] = []
        }
        fieldsByResource[resourceName].push(field)

        // If this is a rate selector, record it as an auto-match target.
        if (resourceName in RATE_SELECTOR_TRIGGER_MAP) {
            if (!rateTargetsByResource[resourceName]) {
                rateTargetsByResource[resourceName] = []
            }
            // The time type comes directly from extraSearchFilters.time_type (set by
            // laborRateColDefCreator from ui:time-type on the schema property).
            // Equipment/material rates don't have a time type — that's fine, buildMatchAction
            // doesn't use it for those rate types.
            const extraSearchFilters = (colDef as iRmbxColDef).extraSearchFilters
            const timeType = extraSearchFilters?.time_type as FieldFormTimeType | undefined
            rateTargetsByResource[resourceName].push({ fieldPath: field, timeType })
        }
    }

    // Build dependency entries for each rate type that has both targets and triggers
    const dependencies: tRateMatchDependency[] = []
    for (const [rateResource, triggerResources] of Object.entries(RATE_SELECTOR_TRIGGER_MAP) as [RateType, string[]][]) {
        const targetFields = rateTargetsByResource[rateResource]
        if (!targetFields || targetFields.length === 0) continue

        const triggerFields: string[] = []
        for (const triggerResource of triggerResources) {
            const fields = fieldsByResource[triggerResource]
            if (fields) {
                triggerFields.push(...fields)
            }
        }
        if (triggerFields.length === 0) continue

        dependencies.push({
            rateResourceName: rateResource,
            triggerFields,
            targetFields,
        })
    }

    return dependencies.length > 0 ? dependencies : undefined
}

export const populateShiftExtraCostCodeColDef = (shiftExtraSettings: Record<string, any>, costCodeId: number): Record<string, any> =>
    shiftExtraSettings.colDefs
        .filter((cd: Record<string, any>) => cd.resourceName === "costCodes")
        .reduce((acc: Record<string, any>, cd: Record<string, any>) => {
            // We use jsonpointer because it needs to be a nested json value
            jsonpointer.set(acc, cd.field as string, costCodeId, true)
            return acc
        }, {} as Record<string, any>)

/**
 * Given a schema and the table context, create an ag-grid table where all the keys in the provided schema
 * are converted into column definitions. By default it also adds project, date, employee/foreman pickers, and
 * a timekeeping status columns.
 * @param schema - Shift extra schema instance (the whole thing, not just the schema)
 * @param context - ag-grid table context
 * @returns ag-grid table settings for a shift extra table
 */
export const getSettingsForShiftExtraSchema = (schema: iEmployeeWorkShiftFormSchema, context: tContext): Record<string, any> => {
    const combinedSchema = getCombinedSchema(schema.schema, schema.ui_schema, {})
    // will return an empty list if the flag is off
    const selectedProjects = getFilteredProjects(context.filters)
    // check if at least one of the selected projects is associated with the given schema project list
    const schemaOnAtleastOneSelectedProject = !!selectedProjects.filter(projectId => schema.projects.includes(projectId)).length
    const parentContainerId = getFlagEnabled("WA-8881-cico-meal-break-min") ? { parentContainerId: "weekly-tk-detail-table-modal" } : {}

    const canAdd = getFlagEnabled("WA-7130-disable-shift-extra-add-row")
        ? !selectedProjects.length
            ? !!schema.projects.length
            : schemaOnAtleastOneSelectedProject
        : /* if flag is off - this should be true */
          true

    return {
        schema: schema,
        tableName: schema.name,
        resources: ["employeeEntries"],
        relatedNames: {
            employeeEntries: {
                employees: ["/employee"],
                projects: ["/project"],
            },
            projects: {},
        },
        colDefs: [
            {
                field: "/schema",
                hide: true,
                cellEditorParams: {
                    default: schema,
                },
            },
            checkboxColDefCreator({}),
            {
                ...projectColDefCreator({
                    hide: filterHasSingleValueSelected(context.filters.projectId),
                    titleFormatter: referenceablesToValueFormatters.projects ? referenceablesToValueFormatters.projects.titleAndStatusFormatter : undefined,
                }),
                extraSearchFilters: {
                    schema_id: schema.id,
                },
            },
            {
                ...dateColDefCreator({}),
                valueSetter: workShiftDateDefaultTimeValueSetter,
            },
            {
                ...employeeColDefCreator({}),
                ignoreRelatedFilters: ["projectId"],
            },
            {
                ...editableTimeCardColDefCreator({
                    headerName: "Time Card",
                    required: true,
                    width: 300,
                    features: context.currentUser.features,
                }),
                ignoreRelatedFilters: ["projectId"],
                extraFilterQueryParams: ["start_date", "foreman_id"],
                extraSearchFilters: { allow_empty: true },
                actionOptions: [getSelectorActionButtons().createNewTimecardButton],
            },
            ...getDefaultColDefsForSchema(combinedSchema, "store/"),
            timekeepingStatusColDefCreator({
                enum: getAvailableStatusValues(context.currentUser.user_role),
            }),
        ],
        gridSettings: {
            defaultColDef: {
                cellClassRules: {
                    "period-locked": lockedPeriodCellClassRule,
                    readonly: (params: CellClassParams) => !timekeepingDataIsEditable(params).canEdit,
                    "alert-cell": alertCellClassRule,
                },
                editable: (params: CellClassParams) => timekeepingDataIsEditable(params).canEdit,
                resizable: true,
                suppressMenu: true,
                unSortIcon: true,
                customTooltip: getFlagEnabled("WA-8192-shift-extra-cost-code-validation") ? timekeepingEntryGeneralErrorsTooltip : timekeepingDisabledTooltip,
                ...parentContainerId,
            },
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            enableRangeSelection: true,
            suppressMultiRangeSelection: true,
            isRowSelectable: (rowNode: RowNode) => timekeepingRowIsEditable(rowNode),
        },
        otherSettings: {
            pasteRangeEnabled: true,
            enableClipboard: true,
            filterByLockedColumns: true,
            hideLockedColumns: true,
            sizeColumnsToFitSize: 915,
            hideCheckboxInHistory: true,
            isInModalTab: true,
            rowLevelErrorDisplay: true,
            highlightErrorRows: true,
            hiddenColumnDefaults: {
                project: "projectId",
            },
            buttons: {
                cell: [],
                row: [...removeBtn],
                table: [
                    {
                        label: "Add Row",
                        icon: "add",
                        action: "addNewRow",
                        disabled: () => !canAdd,
                    },
                    ...(!context.currentUser.features.cico ? showHidePlaceholders(false) : []),
                    editTimecardDetailsAction,
                    ...(context.currentUser.features.cico ? showHidePlaceholders(true) : []),
                ],
            },
        },
    }
}
